import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useModelContext } from '../../context/modelContext';
import { formatMetersPerUnit } from './modelScaleUtils';

type DistanceUnit = 'm' | 'cm' | 'mm' | 'km' | 'in' | 'ft';

const UNIT_TO_METERS: Record<DistanceUnit, number> = {
  m: 1,
  cm: 0.01,
  mm: 0.001,
  km: 1000,
  in: 0.0254,
  ft: 0.3048,
};

type Mode = 'idle' | 'manual' | 'picked';

interface ModelScaleBottomBarProps {
  scalePickActive: boolean;
  /** Non-null when the user has just picked two points — distance between them in model units. */
  pickedUnitDistance: number | null;
  onStartPick: () => void;
  onCancelPick: () => void;
  onCancelPickedEntry: () => void;
  onConfirm: (metersPerUnit: number) => void;
  onClose: () => void;
}

/**
 * Floating bottom toolbar for model-scale calibration.
 *
 * Layout:
 *   ┌─ header ─────────────────────────────────────────────────────────┐
 *   │ Model scale                                [ 1 unit = 1.000 m ]  │
 *   │ <instruction / prompt / pick-hint, switches with mode>           │
 *   ├─ centered actions (idle only) ───────────────────────────────────┤
 *   │         [Enter scale manually]  [Pick two points on model]       │
 *   ├─ input row (manual / picked modes) ──────────────────────────────┤
 *   │ [Picked: X u]  [____] [m ▼]  [Apply]  [Cancel]                   │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * No popup dialogs — everything happens in-place here.
 */
export function ModelScaleBottomBar({
  scalePickActive,
  pickedUnitDistance,
  onStartPick,
  onCancelPick,
  onCancelPickedEntry,
  onConfirm,
  onClose,
}: ModelScaleBottomBarProps) {
  const { t } = useTranslation();
  const { metersPerUnit, scaleCalibrated } = useModelContext();

  const [manualOpen, setManualOpen] = useState(false);
  const mode: Mode = pickedUnitDistance !== null ? 'picked' : manualOpen ? 'manual' : 'idle';

  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<DistanceUnit>('m');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === 'manual') {
      setValue(String(Number(metersPerUnit.toFixed(6))));
      setUnit('m');
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    } else if (mode === 'picked') {
      setValue('');
      setUnit('m');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [mode, metersPerUnit]);

  // Picking takes priority — close any open manual entry
  useEffect(() => {
    if (scalePickActive && manualOpen) setManualOpen(false);
  }, [scalePickActive, manualOpen]);

  const handleApply = () => {
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0) return;
    const meters = num * UNIT_TO_METERS[unit];
    if (mode === 'manual') {
      onConfirm(meters);
      setManualOpen(false);
    } else if (mode === 'picked') {
      if (!pickedUnitDistance || pickedUnitDistance <= 0) return;
      onConfirm(meters / pickedUnitDistance);
    }
  };

  const handleCancelEntry = () => {
    if (mode === 'manual') setManualOpen(false);
    else if (mode === 'picked') onCancelPickedEntry();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEntry();
    }
  };

  const isValid = (() => {
    const n = parseFloat(value);
    if (isNaN(n) || n <= 0) return false;
    if (mode === 'picked' && (!pickedUnitDistance || pickedUnitDistance <= 0)) return false;
    return true;
  })();

  // Subtitle text adapts to mode
  const subtitle = (() => {
    if (scalePickActive) {
      return t(
        'projects.newProject.modelScale.pickHint',
        'Click two points on the model with a known real-world distance (Esc to cancel)',
      );
    }
    if (mode === 'picked') {
      return t('projects.newProject.modelScale.pickedPrompt', 'Enter real-world distance between the two points:');
    }
    if (mode === 'manual') {
      return t('projects.newProject.modelScale.directPrompt', 'Enter real-world size of 1 model unit:');
    }
    return scaleCalibrated
      ? t('projects.newProject.modelScale.recalibrateHint', 'Adjust scale below, or close to keep the current value.')
      : t('projects.newProject.modelScale.instruction', 'Set the real-world size of your model before continuing.');
  })();

  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 bottom-6 z-[150] animate-in fade-in slide-in-from-bottom-2 duration-200"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="bg-[#1E1E1E] border border-white/15 rounded-xl shadow-2xl px-5 py-4 min-w-[560px]">
        {/* Header: title+subtitle on the left, current-scale chip on the right */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-lime-400 font-semibold">
              {t('projects.newProject.modelScale.title', 'Model scale')}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5 max-w-[420px]">{subtitle}</p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-2 bg-[#0a0e14] border border-white/10 rounded-lg px-3 py-1.5">
              <span className="text-[11px] uppercase tracking-wider text-gray-500">
                {t('projects.newProject.modelScale.currentLabel', '1 unit =')}
              </span>
              <span
                className={`text-sm font-mono font-semibold ${scaleCalibrated ? 'text-lime-400' : 'text-gray-300'}`}
              >
                {formatMetersPerUnit(metersPerUnit)}
              </span>
            </div>
            {scaleCalibrated && mode === 'idle' && !scalePickActive && (
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-white transition-colors text-sm px-1 leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Centered action buttons — idle mode only */}
        {mode === 'idle' && (
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setManualOpen(true)}
              disabled={scalePickActive}
              className="h-9 px-5 rounded-lg bg-[#0a0e14] border border-white/10 text-xs text-white hover:border-lime-400/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('projects.newProject.modelScale.enterManually', 'Enter scale manually')}
            </button>
            <button
              type="button"
              onClick={scalePickActive ? onCancelPick : onStartPick}
              className={`h-9 px-5 rounded-lg border text-xs transition-colors ${
                scalePickActive
                  ? 'bg-red-500/20 border-red-500/40 text-red-300 hover:bg-red-500/30'
                  : 'bg-[#0a0e14] border-white/10 text-white hover:border-lime-400/50'
              }`}
            >
              {scalePickActive
                ? t('projects.newProject.modelScale.cancelPick', 'Cancel picking (Esc)')
                : t('projects.newProject.modelScale.pickTwoPoints', 'Pick two points on model')}
            </button>
          </div>
        )}

        {/* Input row — only in manual / picked modes. Appears below the buttons area. */}
        {(mode === 'manual' || mode === 'picked') && (
          <div className="flex items-center gap-2 flex-wrap justify-center">
            {mode === 'picked' && pickedUnitDistance !== null && (
              <span className="text-[11px] text-gray-500 whitespace-nowrap">
                {t('projects.newProject.modelScale.pickedDistanceLabel', 'Picked:')}{' '}
                <span className="text-gray-300 font-mono">{pickedUnitDistance.toFixed(3)} u</span>
              </span>
            )}
            <input
              ref={inputRef}
              type="number"
              step="any"
              min="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              className="w-[140px] h-9 bg-[#0a0e14] border border-white/15 rounded-lg px-3 text-white text-sm outline-none focus:border-[#d4ff33]/50 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as DistanceUnit)}
              className="h-9 bg-[#0a0e14] border border-white/15 rounded-lg px-2 text-white text-sm outline-none cursor-pointer"
            >
              <option value="mm">mm</option>
              <option value="cm">cm</option>
              <option value="m">m</option>
              <option value="km">km</option>
              <option value="in">in</option>
              <option value="ft">ft</option>
            </select>
            <button
              type="button"
              onClick={handleApply}
              disabled={!isValid}
              className={`h-9 px-4 rounded-lg text-xs font-semibold transition-colors ${
                isValid ? 'bg-[#d4ff33] text-black hover:bg-[#e0ff66]' : 'bg-white/5 text-gray-500 cursor-not-allowed'
              }`}
            >
              {t('common.confirm', 'Confirm')}
            </button>
            <button
              type="button"
              onClick={handleCancelEntry}
              className="h-9 px-3 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
